import type { BaseFlowNode, ConditionConfig, FlowNode } from '@lighthouse/shared'
import { getElseHandleId } from '@lighthouse/shared'
import { ScrollArea } from '@mantine/core'
import { useReactFlow } from '@xyflow/react'
import { clone } from 'rambda'
import React, { useCallback, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import styled from 'styled-components'

import { useFlowContainerContext } from '@/contexts/FlowContext'

import type { FlowNodeFormSlice } from './formSlice'
import { NodeConfigureContainer } from './NodeConfigureContainer'

const SCxSettingWrapper = styled(ScrollArea)`
    width: 360px;
    height: 100%;
    overflow-y: auto;
    /* border-left: 1px solid var(--color-gray-200); */

    & .mantine-ScrollArea-viewport > div {
        display: block !important;
    }
`

interface NodeConfigureProps {
    nodeId?: string
}

export const NodeConfigure: React.FC<NodeConfigureProps> = ({ nodeId }) => {
    const { setModalState, setIsChanged } = useFlowContainerContext()
    const { getNodes, updateNodeData, setEdges } = useReactFlow()
    const node = useMemo(() => {
        const nodes = getNodes() as FlowNode[]
        return nodes?.find(node => node.id === nodeId)
    }, [getNodes, nodeId])

    const methods = useForm<FlowNodeFormSlice>({
        mode: 'onChange',
        defaultValues: node?.data as FlowNodeFormSlice | undefined
    })

    const handleChangeNodeConfig = useCallback(
        (nodeId: string, data: BaseFlowNode['data']) => {
            updateNodeData(nodeId, data)
        },
        [updateNodeData]
    )

    const handleRefreshEdges = useCallback(
        (filterGroup?: ConditionConfig['filterGroup']) => {
            if (filterGroup !== undefined && node) {
                const validHandleIds = new Set(filterGroup.map(b => b.id))
                validHandleIds.add(getElseHandleId())
                setEdges(edges =>
                    edges.filter(edge => {
                        if (edge.source === node.id && edge.sourceHandle) {
                            return validHandleIds.has(edge.sourceHandle)
                        }
                        return true
                    })
                )
            } else {
                setEdges(draft => draft)
            }
        },
        [node, setEdges]
    )

    const handleClose = useCallback(() => {
        setModalState({})
    }, [setModalState])

    useEffect(() => {
        if (!node?.id) {
            return
        }
        const { unsubscribe } = methods.watch((formValue, info) => {
            handleChangeNodeConfig(node.id, clone(formValue))
            setIsChanged?.(true)
            if (info.name === 'config') {
                const filterGroup = (formValue.config as ConditionConfig)?.filterGroup
                if (filterGroup) {
                    handleRefreshEdges(filterGroup)
                }
            }
        })
        return () => {
            unsubscribe()
        }
    }, [handleChangeNodeConfig, handleRefreshEdges, methods, node?.id, setIsChanged])

    if (!node) {
        return null
    }

    return (
        <FormProvider {...methods}>
            <SCxSettingWrapper>
                <NodeConfigureContainer node={node} onClose={handleClose} />
            </SCxSettingWrapper>
        </FormProvider>
    )
}
